import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ExtensionInstance } from '@mangovoice/types'

import { useExtensionsApi } from '../api/context'
import { EXTENSION_QUERY_KEYS } from '../api/endpoints'
import type {
  UpdateCallQueuePayload,
  UpdateConferenceRoomPayload,
  UpdateRingGroupPayload,
} from '../types/api.types'

const extractErrorMessage = (data: unknown, fallback: string): string => {
  if (data == null) return fallback
  if (typeof data === 'string') return data || fallback
  if (typeof data !== 'object') return fallback

  const record = data as Record<string, unknown>
  const parameters = record.parameters
  if (Array.isArray(parameters) && parameters.length > 0) {
    const details = parameters
      .map((parameter) => {
        if (parameter == null || typeof parameter !== 'object') return null
        const p = parameter as Record<string, unknown>
        const name = typeof p.name === 'string' ? p.name : 'field'
        const message =
          typeof p.message === 'string' ? p.message : 'Validation error'
        return `${name}: ${message}`
      })
      .filter((value): value is string => Boolean(value))
    if (details.length > 0) return details.join('; ')
  }
  if (typeof record.message === 'string' && record.message.trim() !== '') {
    return record.message
  }
  try {
    return JSON.stringify(data)
  } catch {
    return fallback
  }
}

/**
 * Mutation to update ring group: ring strategy, optional playlist, members.
 */
export const useUpdateRingGroup = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      extensionId,
      payload,
    }: {
      extensionId: number | string
      payload: UpdateRingGroupPayload & {
        name?: string
        number?: string
        omit_from_directory?: boolean
      }
    }) => {
      if (!pbxId) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.updateRingGroup(
        pbxId,
        extensionId,
        payload,
      )
      if (!ok) {
        return Promise.reject(
          new Error(extractErrorMessage(data, 'Failed to update ring group')),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: EXTENSION_QUERY_KEYS.detail(variables.extensionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to update call queue: combines agent and caller settings.
 */
export const useUpdateCallQueue = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      extensionId,
      payload,
    }: {
      extensionId: number | string
      payload: UpdateCallQueuePayload
    }) => {
      if (!pbxId) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.updateCallQueue(
        pbxId,
        extensionId,
        payload,
      )
      if (!ok) {
        return Promise.reject(
          new Error(extractErrorMessage(data, 'Failed to update call queue')),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: EXTENSION_QUERY_KEYS.detail(variables.extensionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to update conference room.
 */
export const useUpdateConferenceRoom = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      extensionId,
      payload,
    }: {
      extensionId: number | string
      payload: UpdateConferenceRoomPayload & {
        name?: string
        number?: string
        omit_from_directory?: boolean
      }
    }) => {
      if (!pbxId) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.updateConferenceRoom(
        pbxId,
        extensionId,
        payload,
      )
      if (!ok) {
        return Promise.reject(
          new Error(
            extractErrorMessage(
              data,
              'Failed to update conference room settings',
            ),
          ),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: EXTENSION_QUERY_KEYS.detail(variables.extensionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}
